export type CrystalType =
  | 'gas'
  | 'liquid'
  | 'native_metal'
  | 'cluster_hexagonal'
  | 'cluster_cubic'
  | 'rough_ore'
  | 'native_nonmetal';

export interface MineralSprite {
  sheet: string;   // nom du fichier dans /public/minerals/ (sans extension)
  row: number;     // index ligne (0-based)
  col: number;     // index colonne (0-based)
  rows: number;    // total lignes dans le sprite sheet
  cols: number;    // total colonnes dans le sprite sheet
  label: string;   // nom affiché
}

export interface NaturalForm {
  description: string;
  appearance: string;
  mainMinerals: { name: string; formula: string }[];
  crystalType: CrystalType;
  mineralColor: string;
  metalness: number;
  roughness: number;
  transparent?: boolean;
  opacity?: number;
  sprites?: MineralSprite[];
}

// atomicNumber -> NaturalForm
export const naturalFormsData: Record<number, NaturalForm> = {
  1: {
    description: "L'hydrogène n'existe pas à l'état natif sur Terre. Il est présent sous forme gazeuse (H₂) en traces dans l'atmosphère et principalement dans l'eau et les composés organiques.",
    appearance: "Gaz incolore, inodore, extrêmement léger",
    mainMinerals: [],
    crystalType: 'gas',
    mineralColor: '#87ceeb',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 0, col: 0, rows: 4, cols: 4, label: 'Tube décharge H (rose-violet)' }],
  },
  2: {
    description: "L'hélium est un gaz noble rare dans l'atmosphère (5 ppm). Il est piégé dans des gisements de gaz naturel, notamment aux États-Unis et au Qatar, où il se forme par désintégration radioactive.",
    appearance: "Gaz incolore, inodore (émet une lumière jaune sous décharge électrique)",
    mainMinerals: [],
    crystalType: 'gas',
    mineralColor: '#fffacd',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 0, col: 1, rows: 4, cols: 4, label: 'Tube décharge He (jaune-orangé)' }],
  },
  3: {
    description: "Le lithium ne se trouve jamais à l'état natif (trop réactif). Il est extrait du spodumène, de la lépidolite et de saumures de lacs salés (Atacama, Bolivie). La demande mondiale explose avec les batteries Li-ion.",
    appearance: "Métal blanc-argenté solide, très léger (moins dense que l'eau)",
    mainMinerals: [
      { name: "Spodumène", formula: "LiAlSi₂O₆" },
      { name: "Lépidolite", formula: "K(Li,Al)₃(Si,Al)₄O₁₀(OH,F)₂" },
      { name: "Pétalite", formula: "LiAlSi₄O₁₀" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#d4c5e2',
    metalness: 0.2,
    roughness: 0.7,
    sprites: [
      { sheet: 'sheet4', row: 0, col: 1, rows: 4, cols: 4, label: 'Lépidolite (mica violet)' },
      { sheet: 'sheet6', row: 3, col: 0, rows: 4, cols: 4, label: 'Kunzite (spodumène rose)' },
    ],
  },
  4: {
    description: "Le béryllium se trouve dans le béryl, un silicate qui produit des gemmes précieuses selon ses impuretés : l'émeraude (vert, avec Cr), l'aigue-marine (bleu, avec Fe), et l'héliodore (jaune). Le chrysobéryl est une autre source.",
    appearance: "Cristaux hexagonaux prismatiques, souvent d'un vert intense (émeraude) ou bleu-vert (aigue-marine)",
    mainMinerals: [
      { name: "Béryl", formula: "Be₃Al₂Si₆O₁₈" },
      { name: "Émeraude (variété)", formula: "Be₃Al₂Si₆O₁₈ + Cr" },
      { name: "Aigue-marine (variété)", formula: "Be₃Al₂Si₆O₁₈ + Fe" },
      { name: "Chrysobéryl", formula: "BeAl₂O₄" },
    ],
    crystalType: 'cluster_hexagonal',
    mineralColor: '#50c878',
    metalness: 0.1,
    roughness: 0.3,
    transparent: true,
    opacity: 0.85,
    sprites: [
      { sheet: 'sheet4', row: 0, col: 0, rows: 4, cols: 4, label: 'Émeraude (Be₃Al₂Si₆O₁₈)' },
      { sheet: 'sheet6', row: 2, col: 0, rows: 4, cols: 4, label: 'Aigue-marine (Be₃Al₂Si₆O₁₈)' },
      { sheet: 'sheet6', row: 3, col: 1, rows: 4, cols: 4, label: 'Alexandrite (BeAl₂O₄)' },
    ],
  },
  5: {
    description: "Le bore se trouve dans des évaporites comme le borax et la kernite, formés par évaporation de lacs salés. La Vallée de la Mort (Californie) et la Turquie (Eskişehir) sont les principales sources mondiales.",
    appearance: "Borax : masses blanches à cristaux prismatiques, aspect poudreux ou vitreux",
    mainMinerals: [
      { name: "Borax", formula: "Na₂B₄O₇·10H₂O" },
      { name: "Kernite", formula: "Na₂B₄O₆(OH)₂·3H₂O" },
      { name: "Colémanite", formula: "Ca₂B₆O₁₁·5H₂O" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#f5f5dc',
    metalness: 0,
    roughness: 0.8,
    sprites: [
      { sheet: 'sheet3', row: 1, col: 0, rows: 4, cols: 4, label: 'Tourmaline noire (B/Fe)' },
      { sheet: 'sheet6', row: 1, col: 0, rows: 4, cols: 4, label: 'Rubellite (tourmaline rose)' },
    ],
  },
  6: {
    description: "Le carbone existe sous deux allotropes naturels : le graphite (feuillets hexagonaux noirs, mou) et le diamant (réseau cubique, transparent et ultra-dur). Il est aussi présent dans les charbons et les carbonates. Le graphite est extrait en Chine et en Inde ; les diamants viennent principalement d'Afrique australe et de Russie.",
    appearance: "Graphite : feuillets noirs métalliques gras. Diamant : cristaux octaédriques incolores à brillants.",
    mainMinerals: [
      { name: "Graphite", formula: "C (hexagonal)" },
      { name: "Diamant", formula: "C (cubique)" },
      { name: "Charbon / anthracite", formula: "C (amorphe)" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#2c2c2c',
    metalness: 0.6,
    roughness: 0.2,
    sprites: [
      { sheet: 'sheet1', row: 0, col: 0, rows: 4, cols: 4, label: 'Graphite (C)' },
      { sheet: 'sheet3', row: 2, col: 2, rows: 4, cols: 4, label: 'Ambre (C)' },
      { sheet: 'sheet6', row: 0, col: 3, rows: 4, cols: 4, label: 'Diamant brut (C)' },
    ],
  },
  7: {
    description: "L'azote constitue 78% de l'atmosphère sous forme de N₂ gazeux. Il ne forme pas de minéraux significatifs à la surface, mais est présent dans le nitre (salpêtre) utilisé historiquement dans la poudre à canon.",
    appearance: "Gaz incolore, inodore — constituant principal de l'air",
    mainMinerals: [
      { name: "Nitre (salpêtre)", formula: "KNO₃" },
    ],
    crystalType: 'gas',
    mineralColor: '#87ceeb',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 0, col: 2, rows: 4, cols: 4, label: 'Tube décharge N (rose-lavande)' }],
  },
  8: {
    description: "L'oxygène est le deuxième gaz atmosphérique (21%) et l'élément le plus abondant de la croûte terrestre. Il est présent dans presque tous les minéraux et roches sous forme d'oxydes, de silicates et de carbonates.",
    appearance: "Gaz incolore et inodore à température ambiante",
    mainMinerals: [
      { name: "Eau", formula: "H₂O" },
      { name: "Quartz", formula: "SiO₂" },
      { name: "Hématite", formula: "Fe₂O₃" },
    ],
    crystalType: 'gas',
    mineralColor: '#b0d4f1',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 0, col: 3, rows: 4, cols: 4, label: 'Tube décharge O (bleu-blanc)' }],
  },
  9: {
    description: "Le fluor est un gaz très réactif, jamais à l'état natif. Le principal minéral fluoré est la fluorite (fluorine), un cube violet à vert très commun. On le trouve aussi dans l'apatite et la cryolithe (rare, Groenland).",
    appearance: "Gaz jaune-verdâtre corrosif. Fluorite : cristaux cubiques violets, verts ou incolores.",
    mainMinerals: [
      { name: "Fluorite (fluorine)", formula: "CaF₂" },
      { name: "Fluoroapatite", formula: "Ca₅(PO₄)₃F" },
      { name: "Cryolithe", formula: "Na₃AlF₆" },
    ],
    crystalType: 'gas',
    mineralColor: '#90ee90',
    metalness: 0,
    roughness: 1,
    sprites: [
      { sheet: 'sheet1', row: 0, col: 3, rows: 4, cols: 4, label: 'Fluorite (CaF₂)' },
      { sheet: 'sheet8', row: 2, col: 3, rows: 4, cols: 4, label: 'Tube décharge F (jaune pâle)' },
    ],
  },
  10: {
    description: "Le néon est un gaz rare atmosphérique (18 ppm). Il ne forme aucun composé chimique. Extrait par liquéfaction de l'air.",
    appearance: "Gaz incolore (émet une lumière orange caractéristique sous décharge électrique)",
    mainMinerals: [],
    crystalType: 'gas',
    mineralColor: '#ff6347',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 1, col: 0, rows: 4, cols: 4, label: 'Tube néon Ne (orange-rouge)' }],
  },
  11: {
    description: "Le sodium ne se trouve jamais à l'état natif (trop réactif avec l'eau). L'halite (sel gemme, NaCl) est l'un des minéraux les plus courants, formé par évaporation de mers anciennes. Les gisements de sel se trouvent dans le monde entier, notamment en Pologne, aux États-Unis et au Pakistan (Khewra).",
    appearance: "Halite : cristaux cubiques blancs à incolores, transparents, goût salé caractéristique",
    mainMinerals: [
      { name: "Halite (sel gemme)", formula: "NaCl" },
      { name: "Trona", formula: "Na₃(CO₃)(HCO₃)·2H₂O" },
      { name: "Natron", formula: "Na₂CO₃·10H₂O" },
    ],
    crystalType: 'cluster_cubic',
    mineralColor: '#f5f5f5',
    metalness: 0.05,
    roughness: 0.4,
    transparent: true,
    opacity: 0.85,
    sprites: [
      { sheet: 'sheet6', row: 1, col: 1, rows: 4, cols: 4, label: 'Lapis-lazuli (Na/Ca silicate)' },
      { sheet: 'sheet6', row: 3, col: 2, rows: 4, cols: 4, label: 'Jadéite (NaAlSi₂O₆)' },
    ],
  },
  12: {
    description: "Le magnésium est un métal très répandu dans l'écorce terrestre. La magnésite et la dolomite sont ses principaux minerais. Il est aussi présent dans l'olivine (roche volcanique verte) et la serpentinite.",
    appearance: "Magnésite : masses blanches compactes. Dolomite : rhomboèdres blancs à roses.",
    mainMinerals: [
      { name: "Magnésite", formula: "MgCO₃" },
      { name: "Dolomite", formula: "CaMg(CO₃)₂" },
      { name: "Olivine", formula: "(Mg,Fe)₂SiO₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#f0f0e0',
    metalness: 0.1,
    roughness: 0.6,
    sprites: [
      { sheet: 'sheet1', row: 3, col: 0, rows: 4, cols: 4, label: 'Malachite (Cu₂CO₃(OH)₂)' },
      { sheet: 'sheet6', row: 2, col: 1, rows: 4, cols: 4, label: 'Péridot (olivine Mg₂SiO₄)' },
      { sheet: 'sheet6', row: 3, col: 3, rows: 4, cols: 4, label: 'Spinelle rouge (MgAl₂O₄)' },
    ],
  },
  13: {
    description: "L'aluminium est le métal le plus abondant de la croûte terrestre. Extrait de la bauxite, un mélange d'oxydes d'aluminium hydratés formé par altération tropicale intense. Les plus grands gisements se trouvent en Australie, Guinée et Brésil. Le corindon pur donne le rubis (rouge) et le saphir (bleu).",
    appearance: "Bauxite : roche rougeâtre à ocre, en masses terreuses ou pisolithiques (grains ronds). Corindon : cristaux hexagonaux durs.",
    mainMinerals: [
      { name: "Gibbsite (bauxite)", formula: "Al(OH)₃" },
      { name: "Boehmite (bauxite)", formula: "AlO(OH)" },
      { name: "Corindon", formula: "Al₂O₃" },
      { name: "Rubis / Saphir (variétés)", formula: "Al₂O₃ + Cr / Fe/Ti" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#cc7722',
    metalness: 0.1,
    roughness: 0.9,
    sprites: [
      { sheet: 'sheet6', row: 0, col: 0, rows: 4, cols: 4, label: 'Rubis (Al₂O₃ + Cr)' },
      { sheet: 'sheet6', row: 0, col: 1, rows: 4, cols: 4, label: 'Saphir (Al₂O₃ + Fe/Ti)' },
      { sheet: 'sheet6', row: 0, col: 2, rows: 4, cols: 4, label: 'Topaze (Al₂SiO₄F₂)' },
    ],
  },
  14: {
    description: "Le silicium est le deuxième élément le plus abondant de la croûte terrestre. Sa forme naturelle la plus pure est le quartz (SiO₂), présent dans presque toutes les roches. Ses variétés colorées incluent l'améthyste (violet), la citrine (jaune), et le quartz rose.",
    appearance: "Quartz : cristaux hexagonaux prismatiques incolores à blancs laiteux, terminés par des pyramides",
    mainMinerals: [
      { name: "Quartz", formula: "SiO₂" },
      { name: "Améthyste (variété)", formula: "SiO₂ + Fe" },
      { name: "Feldspath", formula: "KAlSi₃O₈" },
      { name: "Mica", formula: "KAl₂(AlSi₃O₁₀)(OH)₂" },
    ],
    crystalType: 'cluster_hexagonal',
    mineralColor: '#e8e8f8',
    metalness: 0.05,
    roughness: 0.2,
    transparent: true,
    opacity: 0.8,
    sprites: [
      { sheet: 'sheet2', row: 0, col: 0, rows: 4, cols: 4, label: 'Quartz rose (SiO₂)' },
      { sheet: 'sheet3', row: 2, col: 3, rows: 4, cols: 4, label: 'Opale (SiO₂)' },
    ],
  },
  15: {
    description: "Le phosphore ne se trouve pas à l'état natif. Extrait principalement de l'apatite dans les roches phosphoritiques marines. Le Maroc possède 70% des réserves mondiales. Essentiel pour les engrais agricoles.",
    appearance: "Apatite : cristaux hexagonaux vert, bleu ou brun, parfois incolores, translucides",
    mainMinerals: [
      { name: "Fluoroapatite", formula: "Ca₅(PO₄)₃F" },
      { name: "Hydroxyapatite", formula: "Ca₅(PO₄)₃(OH)" },
      { name: "Phosphorite (roche)", formula: "Ca₃(PO₄)₂" },
    ],
    crystalType: 'cluster_hexagonal',
    mineralColor: '#90ee90',
    metalness: 0.05,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet8', row: 3, col: 0, rows: 4, cols: 4, label: 'Phosphore blanc (lueur froide)' }],
  },
  16: {
    description: "Le soufre se trouve à l'état natif sous forme de cristaux jaunes brillants autour des volcans, fumerolles et dépôts d'évaporites. C'est l'un des rares éléments présents purs dans la nature. Sicile, Indonésie et Mexique sont des sources classiques.",
    appearance: "Cristaux orthorhombiques jaune vif, souvent en pyramides ou masses granuleuses, autour des cratères volcaniques",
    mainMinerals: [
      { name: "Soufre natif", formula: "S₈" },
      { name: "Pyrite", formula: "FeS₂" },
      { name: "Chalcopyrite", formula: "CuFeS₂" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#ffee00',
    metalness: 0,
    roughness: 0.7,
    sprites: [{ sheet: 'sheet3', row: 0, col: 3, rows: 4, cols: 4, label: 'Soufre natif (S)' }],
  },
  17: {
    description: "Le chlore est un gaz jaunâtre très toxique, jamais à l'état natif. Il est omniprésent sous forme de chlorures dans l'halite (sel gemme), les océans et les eaux souterraines salines.",
    appearance: "Gaz jaune-verdâtre toxique. Halite : cristaux cubiques blancs à incolores.",
    mainMinerals: [
      { name: "Halite", formula: "NaCl" },
      { name: "Sylvite", formula: "KCl" },
      { name: "Atacamite", formula: "Cu₂Cl(OH)₃" },
    ],
    crystalType: 'gas',
    mineralColor: '#b2ff59',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 1, col: 1, rows: 4, cols: 4, label: 'Tube décharge Cl (jaune-vert)' }],
  },
  18: {
    description: "L'argon est le troisième gaz atmosphérique (0.93%). Gaz noble, il ne forme aucun composé chimique. Extrait par distillation fractionnée de l'air liquide.",
    appearance: "Gaz incolore, inodore (émet une lumière violette-bleue sous décharge électrique)",
    mainMinerals: [],
    crystalType: 'gas',
    mineralColor: '#d0e4ff',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 1, col: 2, rows: 4, cols: 4, label: 'Tube décharge Ar (violet-mauve)' }],
  },
  19: {
    description: "Le potassium ne se trouve pas à l'état natif. La sylvite (KCl) et la carnallite sont les principaux minerais, formés dans des évaporites marines. Le Canada (Saskatchewan), l'Allemagne et la Russie sont les grands producteurs. Indispensable comme engrais agricole.",
    appearance: "Sylvite : cristaux cubiques incolores à blancs ou rouges, saveur piquante",
    mainMinerals: [
      { name: "Sylvite", formula: "KCl" },
      { name: "Carnallite", formula: "KMgCl₃·6H₂O" },
      { name: "Feldspath potassique", formula: "KAlSi₃O₈" },
    ],
    crystalType: 'cluster_cubic',
    mineralColor: '#ffe4b5',
    metalness: 0.05,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet8', row: 3, col: 1, rows: 4, cols: 4, label: 'Sylvite (KCl, cristaux roses)' }],
  },
  20: {
    description: "Le calcium est très abondant dans la croûte terrestre. La calcite est l'un des minéraux les plus courants, présent dans les calcaires, les marbres, les stalactites. Le gypse est issu de l'évaporation de mers anciennes. L'aragonite constitue les coquilles et les coraux.",
    appearance: "Calcite : cristaux rhomboédriques blancs à incolores, clivage parfait, translucides. Gypse : feuillets soyeux.",
    mainMinerals: [
      { name: "Calcite", formula: "CaCO₃" },
      { name: "Aragonite", formula: "CaCO₃ (orthorhombique)" },
      { name: "Gypse", formula: "CaSO₄·2H₂O" },
      { name: "Fluorite", formula: "CaF₂" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#f0f0f0',
    metalness: 0.05,
    roughness: 0.4,
    transparent: true,
    opacity: 0.85,
    sprites: [
      { sheet: 'sheet3', row: 3, col: 0, rows: 4, cols: 4, label: 'Corail fossile (CaCO₃)' },
      { sheet: 'sheet6', row: 1, col: 3, rows: 4, cols: 4, label: 'Tanzanite (zoisite Ca/Al)' },
    ],
  },
  21: {
    description: "Le scandium est un métal de transition rare, dispersé en traces dans de nombreux minéraux. La thortveitite est le seul minerai vraiment concentré en scandium. L'Ukraine et la Chine sont les principaux producteurs. Utilisé dans les alliages d'aluminium haute performance.",
    appearance: "Thortveitite : cristaux prismatiques gris-vert à brun, rare et peu connu du grand public",
    mainMinerals: [
      { name: "Thortveitite", formula: "Sc₂Si₂O₇" },
      { name: "Kolbeckite", formula: "ScPO₄·2H₂O" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#a0b0a0',
    metalness: 0.3,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet5', row: 0, col: 0, rows: 4, cols: 4, label: 'Thortveitite (Sc₂Si₂O₇)' }],
  },
  22: {
    description: "Le titane se trouve principalement dans le rutile (TiO₂, brun-rouge) et l'ilménite (FeTiO₃, noir), minéraux opaques extraits de sables minéraux côtiers. L'Australie, l'Afrique du Sud et la Chine sont les principaux producteurs. Le titane métal est léger et résistant.",
    appearance: "Rutile : cristaux tétragonaux brun-rouge à noirs, métalliques, souvent en macles en genoux",
    mainMinerals: [
      { name: "Rutile", formula: "TiO₂" },
      { name: "Ilménite", formula: "FeTiO₃" },
      { name: "Anatase", formula: "TiO₂ (tétragonal)" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#8b4513',
    metalness: 0.4,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet4', row: 0, col: 2, rows: 4, cols: 4, label: 'Rutile (TiO₂)' }],
  },
  23: {
    description: "Le vanadium se trouve dans de nombreux minéraux en faibles concentrations. La vanadinite forme de magnifiques cristaux hexagonaux rouge-orange. Il est souvent récupéré comme sous-produit du raffinage du pétrole ou de la fonte du fer.",
    appearance: "Vanadinite : cristaux prismatiques hexagonaux rouge-orange à brun, brillants",
    mainMinerals: [
      { name: "Vanadinite", formula: "Pb₅(VO₄)₃Cl" },
      { name: "Carnotite", formula: "K₂(UO₂)₂(VO₄)₂·3H₂O" },
      { name: "Roscoélite", formula: "KV₂AlSi₃O₁₀(OH)₂" },
    ],
    crystalType: 'cluster_hexagonal',
    mineralColor: '#e05020',
    metalness: 0.1,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet3', row: 2, col: 1, rows: 4, cols: 4, label: 'Vanadinite (Pb₅(VO₄)₃Cl)' }],
  },
  24: {
    description: "Le chrome se trouve principalement dans la chromite, un spinelle brun-noir à métallique. L'Afrique du Sud (Bushveld) et le Kazakhstan possèdent les plus grands gisements. La crocoïte, rouge vif, est un minéral de collection spectaculaire.",
    appearance: "Chromite : masses noir-brunâtres à éclat métallique, granulaires. Crocoïte : cristaux prismatiques rouge vif.",
    mainMinerals: [
      { name: "Chromite", formula: "FeCr₂O₄" },
      { name: "Crocoïte", formula: "PbCrO₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#404040',
    metalness: 0.5,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet1', row: 3, col: 2, rows: 4, cols: 4, label: 'Chrome poli (Cr)' }],
  },
  25: {
    description: "Le manganèse se trouve principalement dans la pyrolusite (MnO₂), un oxyde noir terne. On le trouve aussi dans les nodules polymétalliques des fonds océaniques et la rhodonite rose. L'Afrique du Sud, l'Australie et le Gabon sont les principaux producteurs.",
    appearance: "Pyrolusite : masses noires ternes, en dendrites noires sur les roches (comme du givre) ou opaques",
    mainMinerals: [
      { name: "Pyrolusite", formula: "MnO₂" },
      { name: "Manganite", formula: "MnO(OH)" },
      { name: "Rhodonite", formula: "MnSiO₃" },
      { name: "Rhodochrosite", formula: "MnCO₃" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#1a1a1a',
    metalness: 0.3,
    roughness: 0.8,
    sprites: [
      { sheet: 'sheet1', row: 2, col: 1, rows: 4, cols: 4, label: 'Améthyste (SiO₂+Fe)' },
      { sheet: 'sheet6', row: 2, col: 2, rows: 4, cols: 4, label: 'Rhodonite (MnSiO₃)' },
    ],
  },
  26: {
    description: "Le fer est le métal le plus abondant de la Terre (noyau terrestre). Dans la croûte, il se trouve sous forme d'oxydes (hématite rouge, magnétite noire) et de sulfures (pyrite, 'or des fous'). Les BIF (formations de fer rubané) du Brésil, d'Australie et du Canada sont les principales sources.",
    appearance: "Hématite : masses rouge-grisâtres à éclat métallique. Magnétite : masses noires magnétiques. Pyrite : cubes dorés brillants.",
    mainMinerals: [
      { name: "Hématite", formula: "Fe₂O₃" },
      { name: "Magnétite", formula: "Fe₃O₄" },
      { name: "Pyrite (or des fous)", formula: "FeS₂" },
      { name: "Sidérite", formula: "FeCO₃" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#8b2020',
    metalness: 0.5,
    roughness: 0.6,
    sprites: [
      { sheet: 'sheet1', row: 1, col: 1, rows: 4, cols: 4, label: 'Hématite (Fe₂O₃)' },
      { sheet: 'sheet3', row: 1, col: 3, rows: 4, cols: 4, label: 'Pyrite (FeS₂)' },
      { sheet: 'sheet6', row: 1, col: 2, rows: 4, cols: 4, label: 'Grenat almandin (Fe₃Al₂Si₃O₁₂)' },
    ],
  },
  27: {
    description: "Le cobalt se trouve principalement dans la cobaltite et l'érythrite (fleur de cobalt, rose vif). Il est souvent extrait comme sous-produit du cuivre et du nickel. La RDC (Congo) possède 70% des réserves mondiales, essentielles pour les batteries lithium-ion.",
    appearance: "Cobaltite : cristaux cubiques gris argenté métalliques. Érythrite : efflorescences rose vif caractéristiques.",
    mainMinerals: [
      { name: "Cobaltite", formula: "CoAsS" },
      { name: "Érythrite (fleur de cobalt)", formula: "Co₃(AsO₄)₂·8H₂O" },
      { name: "Linnéite", formula: "Co₃S₄" },
    ],
    crystalType: 'cluster_cubic',
    mineralColor: '#6060c0',
    metalness: 0.6,
    roughness: 0.4,
    sprites: [{ sheet: 'sheet4', row: 0, col: 3, rows: 4, cols: 4, label: 'Érythrite (Co₃(AsO₄)₂·8H₂O)' }],
  },
  28: {
    description: "Le nickel se trouve principalement dans la pentlandite (sulfure brun-bronzé) et les latérites nickélifères (oxydes tropicaux). Du nickel-fer natif existe dans les météorites. L'Indonésie, les Philippines et la Russie sont les principaux producteurs.",
    appearance: "Pentlandite : masses bronzées en association avec la pyrite et la pyrrhotite dans les roches basaltiques",
    mainMinerals: [
      { name: "Pentlandite", formula: "(Fe,Ni)₉S₈" },
      { name: "Millérite", formula: "NiS" },
      { name: "Garniérite (latérite)", formula: "(Ni,Mg)₃Si₂O₅(OH)₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#c8a040',
    metalness: 0.6,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet3', row: 3, col: 3, rows: 4, cols: 4, label: 'Météorite ferreuse (Fe/Ni)' }],
  },
  29: {
    description: "Le cuivre est l'un des rares métaux qui se trouvent à l'état natif (pépites rougeâtres). Ses minerais d'oxydation les plus beaux sont la malachite (vert vif) et l'azurite (bleu intense). La chalcopyrite est le principal minerai économique. Le Chili fournit 25% de la production mondiale.",
    appearance: "Cuivre natif : masses rougeâtres dendritiques. Malachite : vert vif avec zonations. Azurite : bleu intense. Chalcopyrite : jaune-or irisé.",
    mainMinerals: [
      { name: "Cuivre natif", formula: "Cu" },
      { name: "Malachite", formula: "Cu₂CO₃(OH)₂" },
      { name: "Azurite", formula: "Cu₃(CO₃)₂(OH)₂" },
      { name: "Chalcopyrite", formula: "CuFeS₂" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#1e8449',
    metalness: 0.4,
    roughness: 0.5,
    sprites: [
      { sheet: 'sheet1', row: 2, col: 3, rows: 4, cols: 4, label: 'Cuivre natif (Cu)' },
      { sheet: 'sheet3', row: 2, col: 0, rows: 4, cols: 4, label: 'Azurite (Cu₃(CO₃)₂(OH)₂)' },
      { sheet: 'sheet3', row: 0, col: 0, rows: 4, cols: 4, label: 'Chalcopyrite (CuFeS₂)' },
      { sheet: 'sheet6', row: 2, col: 3, rows: 4, cols: 4, label: 'Turquoise (CuAl₆(PO₄)₄(OH)₈)' },
    ],
  },
  30: {
    description: "Le zinc se trouve principalement dans la sphalérite (ZnS), un sulfure brun à noir avec un éclat résineux caractéristique. Le smithsonite (carbonate) peut former de belles sphères bleues ou vertes. La Chine, le Pérou et l'Australie sont les principaux producteurs.",
    appearance: "Sphalérite : cristaux tétraédriques brun-roux à noirs avec éclat résineux, souvent en clivage parfait",
    mainMinerals: [
      { name: "Sphalérite (blende)", formula: "ZnS" },
      { name: "Smithsonite", formula: "ZnCO₃" },
      { name: "Hémimorphite", formula: "Zn₄Si₂O₇(OH)₂·H₂O" },
    ],
    crystalType: 'cluster_cubic',
    mineralColor: '#a08040',
    metalness: 0.4,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet1', row: 3, col: 3, rows: 4, cols: 4, label: 'Bornite / Sphalérite (ZnS)' }],
  },
  31: {
    description: "Le gallium est un métal rare récupéré comme sous-produit du raffinage de la bauxite (aluminium) et de la sphalérite (zinc). Il ne forme pas de mineral propre abondant ; la germanite et la gallite en contiennent des traces. La Chine produit 80% du gallium mondial, indispensable aux semi-conducteurs GaAs et LED.",
    appearance: "Gallite (CuGaS₂) : petits cristaux tétragonaux gris métallique, indiscernables sans analyse. Gallium métal : liquide argenté légèrement au-dessus de la température ambiante (Tf = 29,8 °C).",
    mainMinerals: [
      { name: "Gallite", formula: "CuGaS₂" },
      { name: "Germanite (avec Ga)", formula: "Cu₁₃Fe₂Ge₂S₁₆" },
      { name: "Sphalérite (avec Ga)", formula: "ZnS (trace Ga)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#909090',
    metalness: 0.85,
    roughness: 0.15,
    sprites: [{ sheet: 'sheet7', row: 3, col: 1, rows: 4, cols: 4, label: 'Germanite/gallite (Ga)' }],
  },
  32: {
    description: "Le germanium est un métalloïde dispersé, extrait comme sous-produit du zinc (sphalérite) et du charbon. L'argyrodite, minéral argenté rare, fut le mineral dans lequel Clemens Winkler découvrit l'élément en 1886. La Chine et la RDC sont les principaux producteurs. Essentiel pour les fibres optiques et l'infrarouge.",
    appearance: "Argyrodite : petits cristaux cubiques à éclat métallique gris acier, souvent en masses grenues associées à l'argent",
    mainMinerals: [
      { name: "Argyrodite", formula: "Ag₈GeS₆" },
      { name: "Sphalérite (avec Ge)", formula: "ZnS (trace Ge)" },
      { name: "Renierite", formula: "(Cu,Zn)₁₁(Ge,As)Fe₄S₁₆" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#787878',
    metalness: 0.6,
    roughness: 0.4,
    sprites: [{ sheet: 'sheet7', row: 3, col: 2, rows: 4, cols: 4, label: 'Argyrodite (Ag₈GeS₆)' }],
  },
  33: {
    description: "L'arsenic se trouve à l'état natif sous forme de masses grises métalliques stratifiées. Ses sulfures (réalgar orange-rouge, orpiment jaune) sont très colorés et historiquement utilisés comme pigments. L'arsénopyrite est le principal minerai. Toxique.",
    appearance: "Arsenic natif : masses grises métalliques lamellaires. Réalgar : rouge-orange. Orpiment : jaune-doré.",
    mainMinerals: [
      { name: "Arsenic natif", formula: "As" },
      { name: "Arsénopyrite", formula: "FeAsS" },
      { name: "Réalgar", formula: "AsS" },
      { name: "Orpiment", formula: "As₂S₃" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#808090',
    metalness: 0.5,
    roughness: 0.4,
    sprites: [
      { sheet: 'sheet4', row: 1, col: 0, rows: 4, cols: 4, label: 'Réalgar (AsS)' },
      { sheet: 'sheet4', row: 1, col: 1, rows: 4, cols: 4, label: 'Orpiment (As₂S₃)' },
    ],
  },
  34: {
    description: "Le sélénium se trouve en infimes quantités dans les sulfures de cuivre et de plomb. On le récupère principalement comme sous-produit du raffinage électrolytique du cuivre. Il existe sous forme native en cristaux trigonaux gris ou rouge-brique.",
    appearance: "Sélénium natif : cristaux trigonaux gris métallique (forme a) ou rouge-brique (forme vitreuse)",
    mainMinerals: [
      { name: "Clausthalite", formula: "PbSe" },
      { name: "Naumannite", formula: "Ag₂Se" },
      { name: "Berzelianite", formula: "Cu₂Se" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#cc3300',
    metalness: 0.2,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet4', row: 1, col: 2, rows: 4, cols: 4, label: 'Sélénium natif (Se)' }],
  },
  35: {
    description: "Le brome est l'un des deux seuls éléments liquides à température ambiante avec le mercure. Il ne se trouve pas à l'état natif. On l'extrait de saumures naturelles riches en bromures (mer Morte, puits de pétrole aux États-Unis).",
    appearance: "Liquide brun-rouge foncé fumant, à odeur âcre et toxique",
    mainMinerals: [
      { name: "Bromyrite", formula: "AgBr" },
      { name: "Embolite", formula: "Ag(Cl,Br)" },
    ],
    crystalType: 'liquid',
    mineralColor: '#8b2500',
    metalness: 0.2,
    roughness: 0.1,
    transparent: true,
    opacity: 0.75,
    sprites: [{ sheet: 'sheet8', row: 2, col: 2, rows: 4, cols: 4, label: 'Brome liquide fumant (brun-rouge)' }],
  },
  36: {
    description: "Le krypton est un gaz noble rare de l'atmosphère (1 ppm). Inerte chimiquement, il ne forme aucun composé stable à température ambiante. Extrait par distillation fractionnée de l'air liquide. Utilisé dans les ampoules à incandescence haute performance et les lasers à excimères.",
    appearance: "Gaz incolore (émet une lumière violet pâle à blanc sous décharge électrique)",
    mainMinerals: [],
    crystalType: 'gas',
    mineralColor: '#c8b8e8',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 1, col: 3, rows: 4, cols: 4, label: 'Tube décharge Kr (violet pâle)' }],
  },
  37: {
    description: "Le rubidium est un métal alcalin très réactif extrait comme sous-produit du lithium. Il est présent en traces dans la lépidolite (mica lithique violet) et la pollucite. L'Afrique du Sud et le Zimbabwe en sont les principales sources. La lépidolite rubidifère montre une belle couleur violette due aux ions Mn et Fe.",
    appearance: "Lépidolite rubidifère : lamelles micacées violet à lilas, éclat nacré, souvent en masses granulaires",
    mainMinerals: [
      { name: "Lépidolite (avec Rb)", formula: "K(Li,Al)₃(AlSi₃O₁₀)(OH,F)₂" },
      { name: "Pollucite (avec Rb)", formula: "(Cs,Rb)AlSi₂O₆" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#9b59b6',
    metalness: 0.05,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet7', row: 3, col: 3, rows: 4, cols: 4, label: 'Lépidolite rubidifère (violet)' }],
  },
  38: {
    description: "Le strontium se trouve principalement dans la célestine (SrSO₄), aux cristaux bleu ciel translucides très caractéristiques. La strontianite (carbonate) est plus rare. Le Mexique, la Chine et l'Espagne sont les principaux producteurs. Utilisé en pyrotechnie pour la couleur rouge.",
    appearance: "Célestine : cristaux tabulaires bleu ciel à blancs, translucides, souvent en géodes",
    mainMinerals: [
      { name: "Célestine", formula: "SrSO₄" },
      { name: "Strontianite", formula: "SrCO₃" },
    ],
    crystalType: 'cluster_hexagonal',
    mineralColor: '#87ceeb',
    metalness: 0.05,
    roughness: 0.3,
    transparent: true,
    opacity: 0.85,
    sprites: [{ sheet: 'sheet4', row: 1, col: 3, rows: 4, cols: 4, label: 'Célestite (SrSO₄)' }],
  },
  39: {
    description: "L'yttrium est une terre rare légère présente dans le xénotime (YPO₄) et la gadolinite. Le xénotime forme des cristaux prismatiques brun-jaune à brun-rouge caractéristiques. La Chine fournit l'essentiel de la production mondiale. L'yttrium est indispensable aux phosphores rouges des écrans et aux lasers YAG.",
    appearance: "Xénotime (YPO₄) : cristaux prismatiques tétragonaux brun-jaune à brun-rouge, éclat résineux à vitreux",
    mainMinerals: [
      { name: "Xénotime", formula: "YPO₄" },
      { name: "Gadolinite", formula: "Y₂Fe²⁺Be₂Si₂O₁₀" },
      { name: "Monazite (avec Y)", formula: "(Ce,La,Y)PO₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#c8a040',
    metalness: 0.2,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet7', row: 2, col: 2, rows: 4, cols: 4, label: 'Xénotime (YPO₄, brun-jaune)' }],
  },
  40: {
    description: "Le zirconium se trouve principalement dans le zircon (ZrSiO₄), un des minéraux les plus anciens de la Terre (4.4 Ga, Jack Hills, Australie). Très résistant à l'altération, il est utilisé en datation géochronologique. Les variétés colorées sont des gemmes populaires.",
    appearance: "Zircon : cristaux tétragonaux incolores à bruns, à éclat adamantin brillant, très durs",
    mainMinerals: [
      { name: "Zircon", formula: "ZrSiO₄" },
      { name: "Baddéleyite", formula: "ZrO₂" },
    ],
    crystalType: 'cluster_cubic',
    mineralColor: '#9370db',
    metalness: 0.15,
    roughness: 0.3,
    transparent: true,
    opacity: 0.85,
    sprites: [{ sheet: 'sheet4', row: 2, col: 0, rows: 4, cols: 4, label: 'Zircon (ZrSiO₄)' }],
  },
  41: {
    description: "Le niobium se trouve principalement dans la columbite (coltan), un oxyde noir lourd associé à la tantalite. Le Brésil possède 90% des réserves mondiales, essentiellement dans le complexe carbonatitique de Araxa. Indispensable pour les aciers haute résistance et les aimants supraconducteurs.",
    appearance: "Columbite : cristaux prismatiques à tabulaires noirs à éclat subadamantin à métallique, très lourds",
    mainMinerals: [
      { name: "Columbite-(Fe)", formula: "Fe²⁺Nb₂O₆" },
      { name: "Coltan (colombo-tantalite)", formula: "(Fe,Mn)(Nb,Ta)₂O₆" },
      { name: "Pyrochlore", formula: "(Na,Ca)₂Nb₂O₆F" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#1a1a20',
    metalness: 0.7,
    roughness: 0.4,
    sprites: [{ sheet: 'sheet7', row: 2, col: 3, rows: 4, cols: 4, label: 'Columbite (noir métal)' }],
  },
  42: {
    description: "Le molybdène se trouve principalement dans la molybdénite (MoS₂), un sulfure lamellaire gris argenté ressemblant au graphite. Il se forme dans les porphyres cuprifères. La Chine fournit 40% de la production mondiale. La wulfénite, jaune-orange, est une gemme rare.",
    appearance: "Molybdénite : feuillets hexagonaux gris argenté souples et luisants, éclat métallique",
    mainMinerals: [
      { name: "Molybdénite", formula: "MoS₂" },
      { name: "Wulfénite", formula: "PbMoO₄" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#a0a0b0',
    metalness: 0.8,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet3', row: 3, col: 1, rows: 4, cols: 4, label: 'Wulfénite (PbMoO₄)' }],
  },
  43: {
    description: "Le technétium est le premier élément synthétique de la table périodique. Il n'existe pas à l'état naturel en quantités mesurables (demi-vie max : 4,2 Ma pour ⁹⁸Tc). Produit dans les réacteurs nucléaires par fission de l'uranium, il est crucial en médecine nucléaire : le ⁹⁹ᵐTc est utilisé dans 85% des scintigraphies médicales mondiales.",
    appearance: "Pastille métallique gris argenté brillant en laboratoire, légère aura radioactive bleue, cible de cyclotron",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#909098',
    metalness: 0.9,
    roughness: 0.15,
    sprites: [{ sheet: 'sheet9', row: 0, col: 0, rows: 4, cols: 4, label: 'Technétium métal (pastille lab)' }],
  },
  44: {
    description: "Le ruthénium est un métal de transition extrêmement rare du groupe du platine. Il se trouve en traces dans les minerais de platine, principalement sous forme de laurite (RuS₂). La Russie et l'Afrique du Sud sont les principaux producteurs. Utilisé comme catalyseur et dans les contacts électriques.",
    appearance: "Laurite : petits cristaux cubiques noirs à éclat métallique, associés aux minerais de platine",
    mainMinerals: [
      { name: "Laurite", formula: "RuS₂" },
      { name: "Ruthénium natif", formula: "Ru" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#606060',
    metalness: 0.85,
    roughness: 0.3,
    sprites: [{ sheet: 'sheet5', row: 0, col: 1, rows: 4, cols: 4, label: 'Laurite (RuS₂)' }],
  },
  45: {
    description: "Le rhodium est l'un des métaux les plus rares et précieux au monde. Il se trouve en infimes quantités dans les minerais de platine et de nickel. L'Afrique du Sud en produit 80%. Essentiel pour les pots catalytiques automobiles (convertisseurs NOx).",
    appearance: "Rhodium natif : petites pépites ou grains argentés brillants, extrêmement denses, associés à la platine",
    mainMinerals: [
      { name: "Rhodium natif", formula: "Rh" },
      { name: "Hollingworthite", formula: "RhAsS" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#e8e8e8',
    metalness: 0.99,
    roughness: 0.03,
    sprites: [{ sheet: 'sheet5', row: 0, col: 2, rows: 4, cols: 4, label: 'Rhodium natif (Rh)' }],
  },
  46: {
    description: "Le palladium est un métal précieux du groupe du platine, découvert en 1803. Il se trouve à l'état natif en petites pépites ou en alliage avec la platine. L'Afrique du Sud et la Russie fournissent 80% de la production. Indispensable pour les pots catalytiques et l'électronique.",
    appearance: "Palladium natif : pépites argentées brillantes, plus légères que la platine (densité 12 vs 21)",
    mainMinerals: [
      { name: "Palladium natif", formula: "Pd" },
      { name: "Stibiopalladinite", formula: "Pd₅Sb₂" },
      { name: "Braggite", formula: "(Pt,Pd,Ni)S" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#d0d0d0',
    metalness: 0.97,
    roughness: 0.05,
    sprites: [{ sheet: 'sheet5', row: 0, col: 3, rows: 4, cols: 4, label: 'Palladium natif (Pd)' }],
  },
  47: {
    description: "L'argent se trouve à l'état natif sous forme de filaments, lamelles ou dendrites blanc brillant. L'argentite et la pyrargyrite sont aussi des minerais importants. Le Mexique, le Pérou et la Chine sont les principaux producteurs. L'argent accompagne souvent les minerais de cuivre et de plomb.",
    appearance: "Argent natif : masses filiformes, lamellaires ou dendritiques blanc brillant métallique",
    mainMinerals: [
      { name: "Argent natif", formula: "Ag" },
      { name: "Argentite / Acanthite", formula: "Ag₂S" },
      { name: "Pyrargyrite (argent rouge)", formula: "Ag₃SbS₃" },
      { name: "Chlorargyrite", formula: "AgCl" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#c0c0c0',
    metalness: 0.97,
    roughness: 0.08,
    sprites: [{ sheet: 'sheet3', row: 0, col: 2, rows: 4, cols: 4, label: 'Argent natif (Ag)' }],
  },
  48: {
    description: "Le cadmium est un métal toxique présent en traces dans les minerais de zinc (sphalérite). La greenockite (CdS) est son seul minéral propre, avec une couleur jaune vif caractéristique. Il est récupéré comme sous-produit du raffinage du zinc. Utilisé dans les batteries NiCd et les pigments jaunes.",
    appearance: "Greenockite : poudre ou croûtes jaune vif à orange sur la sphalérite, éclat adamantin",
    mainMinerals: [
      { name: "Greenockite", formula: "CdS" },
      { name: "Otavite", formula: "CdCO₃" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#ffd700',
    metalness: 0.1,
    roughness: 0.7,
    sprites: [{ sheet: 'sheet5', row: 1, col: 0, rows: 4, cols: 4, label: 'Greenockite (CdS)' }],
  },
  49: {
    description: "L'indium est un métal très rare récupéré exclusivement comme sous-produit du raffinage du zinc (sphalérite). Il ne forme aucun mineral propre exploitable. La Chine produit 60% de l'indium mondial. Indispensable pour l'ITO (oxyde d'indium-étain), le revêtement conducteur transparent des écrans LCD et OLED.",
    appearance: "Indium métal : lingots blanc brillant argenté légèrement bleuté, très mou (rayable à l'ongle), émet un cri d'étain",
    mainMinerals: [
      { name: "Indium dans sphalérite", formula: "ZnS (trace In)" },
      { name: "Roquesite", formula: "CuInS₂" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#d8d8e8',
    metalness: 0.9,
    roughness: 0.1,
    sprites: [{ sheet: 'sheet7', row: 3, col: 0, rows: 4, cols: 4, label: 'Indium métal (blanc étain)' }],
  },
  50: {
    description: "L'étain se trouve principalement dans la cassitérite, un oxyde brun-rouge à noir. L'exploitation de la cassitérite remonte à 3000 ans av. J.-C. (Bronze = Cu + Sn). La Chine, l'Indonésie et le Myanmar sont les principaux producteurs.",
    appearance: "Cassitérite : cristaux tétragonaux brun-rouge à noirs, à éclat adamantin vif, très lourds",
    mainMinerals: [
      { name: "Cassitérite", formula: "SnO₂" },
      { name: "Stannite", formula: "Cu₂FeSnS₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#8b4513',
    metalness: 0.3,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet4', row: 2, col: 1, rows: 4, cols: 4, label: 'Cassitérite (SnO₂)' }],
  },
  51: {
    description: "L'antimoine se trouve principalement dans la stibine (Sb₂S₃), un sulfure en cristaux prismatiques argentés caractéristiques. L'antimoine natif existe aussi en petites quantités. La Chine fournit 85% de la production mondiale.",
    appearance: "Stibine : cristaux prismatiques allongés et striés gris acier à éclat métallique brillant",
    mainMinerals: [
      { name: "Stibine (antimonite)", formula: "Sb₂S₃" },
      { name: "Antimoine natif", formula: "Sb" },
      { name: "Boulangerite", formula: "Pb₅Sb₄S₁₁" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#708090',
    metalness: 0.6,
    roughness: 0.3,
    sprites: [{ sheet: 'sheet2', row: 3, col: 0, rows: 4, cols: 4, label: 'Antimoine cristallisé (Sb)' }],
  },
  52: {
    description: "Le tellure est un métalloïde rare, extrait comme sous-produit du raffinage du cuivre. Il se trouve dans des tellurures natifs et des tellurites aurifères (calaverite, sylvanite). Ces tellurures d'or ont causé la ruée vers l'or de Kalgoorlie (Australie, 1893).",
    appearance: "Tellure natif : cristaux trigonaux blanc-argent brillants, striés longitudinalement",
    mainMinerals: [
      { name: "Tellure natif", formula: "Te" },
      { name: "Calaverite", formula: "AuTe₂" },
      { name: "Sylvanite", formula: "(Au,Ag)Te₂" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#c0c080',
    metalness: 0.5,
    roughness: 0.3,
    sprites: [{ sheet: 'sheet5', row: 3, col: 3, rows: 4, cols: 4, label: 'Calaverite (AuTe₂)' }],
  },
  53: {
    description: "L'iode se trouve principalement dans les eaux souterraines salines et les gisements de nitrates chiliens (caliche). La lautarite et l'iodyrite sont les principaux minéraux. Le Chili et le Japon sont les principaux producteurs. L'iode peut former des cristaux gris-violet métalliques.",
    appearance: "Iode natif : cristaux rhomboédriques gris-violet métallique, sublimant en vapeur violette",
    mainMinerals: [
      { name: "Iodyrite (iodargyrite)", formula: "AgI" },
      { name: "Lautarite", formula: "Ca(IO₃)₂" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#483d8b',
    metalness: 0.3,
    roughness: 0.4,
    sprites: [{ sheet: 'sheet4', row: 2, col: 2, rows: 4, cols: 4, label: 'Iode natif (I₂)' }],
  },
  54: {
    description: "Le xénon est un gaz noble rare de l'atmosphère (0.087 ppm). Malgré son inertie chimique, il forme quelques composés avec le fluor. Extrait par distillation fractionnée de l'air liquide. Utilisé dans les phares au xénon (HID), les lasers excimères et les propulseurs ioniques spatiaux.",
    appearance: "Gaz incolore (émet une lumière bleu-blanc intense sous décharge électrique)",
    mainMinerals: [],
    crystalType: 'gas',
    mineralColor: '#a0c8f8',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 2, col: 0, rows: 4, cols: 4, label: 'Tube décharge Xe (bleu-blanc)' }],
  },
  55: {
    description: "Le césium est un métal alcalin mou, argenté, qui s'enflamme spontanément à l'air. Son principal minerai est la pollucite, un silicate rare trouvé principalement au Manitoba (Canada) et en Namibie. Le césium-137 (radioactif) est un sous-produit des réacteurs nucléaires. Utilisé dans les horloges atomiques.",
    appearance: "Pollucite : masses vitreuses incolores à blanches, aspect de verre ou de quartz, sans clivage",
    mainMinerals: [
      { name: "Pollucite", formula: "(Cs,Na)₂Al₂Si₄O₁₂·H₂O" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#e8e8d0',
    metalness: 0.05,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet5', row: 1, col: 1, rows: 4, cols: 4, label: 'Pollucite (Cs silicate)' }],
  },
  56: {
    description: "Le baryum se trouve principalement dans la baryte (BaSO₄), un sulfate blanc très dense (densité 4.5). Sa grande densité la rend indispensable comme fluide de forage dans l'industrie pétrolière. Elle forme des 'roses des sables' dans les déserts.",
    appearance: "Baryte : cristaux tabulaires blancs à incolores, en rosettes ou crêtes cristallines, étonnamment lourd",
    mainMinerals: [
      { name: "Baryte (barytine)", formula: "BaSO₄" },
      { name: "Withérite", formula: "BaCO₃" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#f0f0f0',
    metalness: 0.05,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet4', row: 2, col: 3, rows: 4, cols: 4, label: 'Baryte (BaSO₄)' }],
  },
  57: {
    description: "Le lanthane est présent dans les minéraux des terres rares, principalement la monazite et la bastnäsite. La monazite est souvent trouvée dans des sables côtiers (Kerala, Inde ; Brésil). La Chine possède 60% des réserves mondiales de terres rares.",
    appearance: "Bastnäsite : cristaux hexagonaux brun-jaune à éclat vitreux. Monazite : grains brun-orange arrondis.",
    mainMinerals: [
      { name: "Monazite", formula: "(Ce,La,Nd,Th)PO₄" },
      { name: "Bastnäsite", formula: "(Ce,La)(CO₃)F" },
      { name: "Allanite", formula: "(Ce,Ca,Y)₂(Al,Fe)₃(SiO₄)₃(OH)" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#b8860b',
    metalness: 0.2,
    roughness: 0.7,
    sprites: [{ sheet: 'sheet4', row: 3, col: 0, rows: 4, cols: 4, label: 'Bastnäsite (La/Ce)' }],
  },
  58: {
    description: "Le cérium est le plus abondant des terres rares. Il se trouve dans la monazite et la bastnäsite, minéraux des carbonatites et des sables côtiers. La Chine, l'Inde et le Brésil sont les principaux producteurs.",
    appearance: "Monazite : cristaux monocliniques brun-orange, souvent en grains arrondis dans les sables minéraux",
    mainMinerals: [
      { name: "Monazite", formula: "(Ce,La,Nd,Th)PO₄" },
      { name: "Bastnäsite", formula: "(Ce,La)(CO₃)F" },
      { name: "Allanite", formula: "(Ce,Ca,Y)₂(Al,Fe)₃(SiO₄)₃(OH)" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#d4a0a0',
    metalness: 0.2,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet5', row: 1, col: 2, rows: 4, cols: 4, label: 'Monazite (Ce/La PO₄)' }],
  },
  59: {
    description: "Le praséodyme est une terre rare présente dans la monazite et la bastnäsite. Sa bastnäsite a une belle couleur verte caractéristique due aux ions Pr³⁺. Utilisé dans les verres spéciaux filtrant la lumière jaune pour les soudeurs et dans les aimants permanents NdFeB.",
    appearance: "Bastnäsite à Pr : cristaux hexagonaux vert à jaune-vert, éclat vitreux à résineux",
    mainMinerals: [
      { name: "Monazite", formula: "(Ce,La,Nd,Pr,Th)PO₄" },
      { name: "Bastnäsite", formula: "(Ce,La,Pr)(CO₃)F" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#5ca05c',
    metalness: 0.1,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet5', row: 1, col: 3, rows: 4, cols: 4, label: 'Bastnäsite (Pr, verte)' }],
  },
  60: {
    description: "Le néodyme est la terre rare la plus utilisée industriellement. Ses aimants NdFeB (néodyme-fer-bore) sont les plus puissants au monde et équipent moteurs électriques et éoliennes. Extrait de la monazite et de la bastnäsite, principalement en Chine (85% de la production).",
    appearance: "Néodyme métal : lingots argentés brillants légèrement bleutés, s'oxydant rapidement en violet en surface",
    mainMinerals: [
      { name: "Monazite", formula: "(Ce,La,Nd,Th)PO₄" },
      { name: "Bastnäsite", formula: "(Ce,La,Nd)(CO₃)F" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#7070c0',
    metalness: 0.8,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet5', row: 2, col: 0, rows: 4, cols: 4, label: 'Néodyme métal (Nd)' }],
  },
  61: {
    description: "Le prométhium est la seule terre rare sans isotope stable. Tous ses isotopes sont radioactifs, le plus long vécu (¹⁴⁵Pm) ayant une demi-vie de 17,7 ans. Produit en très petites quantités dans les réacteurs nucléaires. Utilisé dans des générateurs d'énergie bêta miniatures (pacemakers, engins spatiaux) et des peintures luminescentes.",
    appearance: "Fiole de verre scellée contenant une poudre rose-violacée luminescente, douce lueur radioactive froide",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#c080d0',
    metalness: 0.5,
    roughness: 0.3,
    sprites: [{ sheet: 'sheet9', row: 0, col: 1, rows: 4, cols: 4, label: 'Prométhium (fiole luminescente)' }],
  },
  62: {
    description: "Le samarium est une terre rare présente dans la monazite et la bastnäsite. La monazite à Sm a une teinte beige-brun caractéristique. Connu pour ses aimants SmCo (samarium-cobalt), les plus résistants à la démagnetisation thermique. La Chine est le principal producteur.",
    appearance: "Monazite à Sm : cristaux monocliniques beige-brun, parfois orangés, éclat vitreux à résineux",
    mainMinerals: [
      { name: "Monazite", formula: "(Ce,La,Nd,Sm,Th)PO₄" },
      { name: "Bastnäsite", formula: "(Ce,La,Sm)(CO₃)F" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#c8a870',
    metalness: 0.2,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet7', row: 0, col: 0, rows: 4, cols: 4, label: 'Monazite à Sm (beige-brun)' }],
  },
  63: {
    description: "L'europium est la terre rare la plus réactive, s'oxydant instantanément à l'air. On le trouve en traces dans la bastnäsite et la monazite. Ses ions Eu²⁺ et Eu³⁺ émettent des luminescences rouge et bleue intenses, essentielles pour les phosphores des écrans et les billets de banque anti-contrefaçon.",
    appearance: "Bastnäsite à Eu : cristaux hexagonaux crème à jaune pâle, éclat vitreux, indiscernables sans analyse chimique",
    mainMinerals: [
      { name: "Bastnäsite (avec Eu)", formula: "(Ce,La,Eu)(CO₃)F" },
      { name: "Monazite (avec Eu)", formula: "(Ce,La,Eu)PO₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#e8e0a0',
    metalness: 0.1,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet7', row: 0, col: 1, rows: 4, cols: 4, label: 'Bastnäsite Eu (crème-jaune)' }],
  },
  64: {
    description: "Le gadolinium est une terre rare aux propriétés magnétiques exceptionnelles. La gadolinite, minéral noir-verdâtre dense, est sa principale source historique. Ses ions Gd³⁺ sont utilisés comme agents de contraste en IRM. La Chine domine la production mondiale.",
    appearance: "Gadolinite : masses compactes noir-verdâtres à éclat résineux à vitreux, souvent métamictes (amorphes par radioactivité)",
    mainMinerals: [
      { name: "Gadolinite", formula: "Y₂Fe²⁺Be₂Si₂O₁₀ (avec Gd)" },
      { name: "Monazite (avec Gd)", formula: "(Ce,Gd,La)PO₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#2a3020',
    metalness: 0.3,
    roughness: 0.7,
    sprites: [{ sheet: 'sheet7', row: 0, col: 2, rows: 4, cols: 4, label: 'Gadolinite (noir-verdâtre)' }],
  },
  65: {
    description: "Le terbium est une terre rare rare extraite de la xénotime et de la gadolinite. Le métal poli a un aspect argenté brillant. Il est indispensable pour les aimants de traction (Tb dans NdFeB améliore la résistance thermique) et les matériaux magnétostrictifs Terfenol-D. Production quasi-exclusivement chinoise.",
    appearance: "Terbium métal poli : lingots argentés brillants, légèrement grisâtres, s'oxydant lentement en surface",
    mainMinerals: [
      { name: "Xénotime (avec Tb)", formula: "YPO₄ (trace Tb)" },
      { name: "Gadolinite (avec Tb)", formula: "Y₂FeBe₂Si₂O₁₀ (trace Tb)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#c0c0b8',
    metalness: 0.9,
    roughness: 0.1,
    sprites: [{ sheet: 'sheet7', row: 0, col: 3, rows: 4, cols: 4, label: 'Terbium métal poli (argenté)' }],
  },
  66: {
    description: "Le dysprosium est une terre rare à haut pouvoir d'absorption des neutrons, présente dans la xénotime et la monazite. Son métal a un reflet légèrement bleuté. Essentiel pour les aimants NdFeB haute température (véhicules électriques, éoliennes) et les barres de contrôle nucléaires.",
    appearance: "Dysprosium métal : lingots argentés avec reflet bleuté subtil, s'oxydant lentement à l'air humide",
    mainMinerals: [
      { name: "Xénotime (avec Dy)", formula: "YPO₄ (trace Dy)" },
      { name: "Monazite (avec Dy)", formula: "(Ce,La,Dy)PO₄" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#a8b8c8',
    metalness: 0.88,
    roughness: 0.12,
    sprites: [{ sheet: 'sheet7', row: 1, col: 0, rows: 4, cols: 4, label: 'Dysprosium métal (argenté bleuté)' }],
  },
  67: {
    description: "L'holmium est une terre rare aux propriétés magnétiques parmi les plus fortes de tous les éléments. Le xénotime en contient de petites quantités. Son métal est argenté doux. Utilisé dans les guides d'ondes magnétiques, les lasers médicaux (Ho:YAG à 2,1 µm) et les aimants supraconducteurs.",
    appearance: "Xénotime à Ho : cristaux prismatiques brun-rose à brun-rougeâtre, éclat résineux à vitreux",
    mainMinerals: [
      { name: "Xénotime (avec Ho)", formula: "YPO₄ (trace Ho)" },
      { name: "Gadolinite (avec Ho)", formula: "Y₂FeBe₂Si₂O₁₀ (trace Ho)" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#c09080',
    metalness: 0.2,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet7', row: 1, col: 1, rows: 4, cols: 4, label: 'Xénotime Ho (brun-rose)' }],
  },
  68: {
    description: "L'erbium est une terre rare rose caractéristique dont l'oxyde Er₂O₃ est un pigment rose pâle distinctif. On le trouve dans la xénotime et la gadolinite. Essentiel pour les amplificateurs à fibre optique erbium (EDFA) qui permettent les télécommunications à longue distance.",
    appearance: "Erbium oxyde (Er₂O₃) : poudre rose pâle caractéristique. Métal : argenté brillant.",
    mainMinerals: [
      { name: "Xénotime (avec Er)", formula: "YPO₄ (trace Er)" },
      { name: "Gadolinite (avec Er)", formula: "Y₂FeBe₂Si₂O₁₀ (trace Er)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#e8a0b0',
    metalness: 0.3,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet7', row: 1, col: 2, rows: 4, cols: 4, label: 'Erbium oxyde (rose pâle)' }],
  },
  69: {
    description: "Le thulium est la terre rare naturelle la plus rare et la plus coûteuse. Le métal a un aspect argenté brillant. Il se trouve en infimes traces dans la monazite et la xénotime. Utilisé pour les sources de rayons X portables (¹⁷⁰Tm) et des lasers solides. Production quasi-anecdotique.",
    appearance: "Thulium métal : lingots argentés brillants très purs, surface lisse légèrement bleutée",
    mainMinerals: [
      { name: "Monazite (trace Tm)", formula: "(Ce,La,Tm)PO₄" },
      { name: "Xénotime (trace Tm)", formula: "YPO₄ (trace Tm)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#c8c8d0',
    metalness: 0.92,
    roughness: 0.08,
    sprites: [{ sheet: 'sheet7', row: 1, col: 3, rows: 4, cols: 4, label: 'Thulium métal (argenté)' }],
  },
  70: {
    description: "L'ytterbium est une terre rare présente dans l'ytterbite (ancienne gadolinite) et la monazite. La gadolinite brun-noirâtre est sa source historique, découverte à Ytterby (Suède). Utilisé dans les horloges atomiques les plus précises, les lasers fibres et les capteurs de pression.",
    appearance: "Gadolinite (ytterbite) : masses compactes brun à brun-noirâtre, éclat résineux, souvent métamictes",
    mainMinerals: [
      { name: "Gadolinite (ytterbite)", formula: "Y₂Fe²⁺Be₂Si₂O₁₀ (avec Yb)" },
      { name: "Monazite (avec Yb)", formula: "(Ce,La,Yb)PO₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#6b4c2a',
    metalness: 0.25,
    roughness: 0.65,
    sprites: [{ sheet: 'sheet7', row: 2, col: 0, rows: 4, cols: 4, label: 'Ytterbite / gadolinite (brun)' }],
  },
  71: {
    description: "Le lutétium est la dernière et la plus dense des terres rares. Le métal blanc argenté est extrait de la monazite et de l'ytterbite. Très cher en raison de sa rareté extrême. Utilisé dans les scintillateurs PET-scan (Lu₂SiO₅:Ce) et les catalyseurs de raffinage.",
    appearance: "Lutétium métal : lingots blanc argenté brillant, l'un des métaux de terres rares les plus denses",
    mainMinerals: [
      { name: "Monazite (trace Lu)", formula: "(Ce,La,Lu)PO₄" },
      { name: "Gadolinite (trace Lu)", formula: "Y₂FeBe₂Si₂O₁₀ (trace Lu)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#d8d8d0',
    metalness: 0.92,
    roughness: 0.08,
    sprites: [{ sheet: 'sheet7', row: 2, col: 1, rows: 4, cols: 4, label: 'Lutetium métal (blanc argenté)' }],
  },
  72: {
    description: "Le hafnium est chimiquement quasi-identique au zirconium et toujours présent dans le zircon (1-3%). Il est séparé industriellement du zirconium par distillation fractionnée. La Russie et l'Australie sont les principaux producteurs. Utilisé dans les barres de contrôle des réacteurs nucléaires.",
    appearance: "Zircon hafnifère : cristaux tétragonaux brun-roux brillants, indiscernables visuellement du zircon pur",
    mainMinerals: [
      { name: "Zircon (avec Hf)", formula: "Zr₁₋ₓHfₓSiO₄" },
      { name: "Hafnon", formula: "HfSiO₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#9080a0',
    metalness: 0.5,
    roughness: 0.4,
    sprites: [{ sheet: 'sheet5', row: 2, col: 1, rows: 4, cols: 4, label: 'Zircon hafnifère (Hf)' }],
  },
  73: {
    description: "Le tantale se trouve principalement dans le coltan, un mélange de colombite et de tantalite. La RDC (Congo) fournit 60% de la production mondiale. Le tantale est essentiel pour les condensateurs des appareils électroniques (smartphones, ordinateurs). Métal de conflit.",
    appearance: "Tantalite : cristaux prismatiques ou tabulaires noirs à éclat subadamantin, très lourds (densité 8)",
    mainMinerals: [
      { name: "Tantalite", formula: "(Fe,Mn)Ta₂O₆" },
      { name: "Coltan (colombo-tantalite)", formula: "(Fe,Mn)(Nb,Ta)₂O₆" },
      { name: "Microlite", formula: "(Na,Ca)₂Ta₂O₆(O,OH,F)" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#2a2a35',
    metalness: 0.7,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet4', row: 3, col: 1, rows: 4, cols: 4, label: 'Coltan/Tantalite (Ta)' }],
  },
  74: {
    description: "Le tungstène (wolframm) se trouve dans la wolframite (brun-noir) et la scheelite (blanche, fluorescente sous UV). La wolframite est lourde et foncée ; la scheelite brille en bleu-blanc sous lampe UV. La Chine produit 80% du tungstène mondial.",
    appearance: "Wolframite : cristaux prismatiques brun-noirs, très lourds, à éclat métallique subadamantin",
    mainMinerals: [
      { name: "Wolframite", formula: "(Fe,Mn)WO₄" },
      { name: "Scheelite", formula: "CaWO₄" },
      { name: "Hübnerite", formula: "MnWO₄" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#505060',
    metalness: 0.7,
    roughness: 0.4,
    sprites: [
      { sheet: 'sheet4', row: 3, col: 2, rows: 4, cols: 4, label: 'Wolframite ((Fe,Mn)WO₄)' },
      { sheet: 'sheet4', row: 3, col: 3, rows: 4, cols: 4, label: 'Scheelite (CaWO₄)' },
    ],
  },
  75: {
    description: "Le rhénium est l'un des éléments les plus rares de la croûte terrestre. Il se trouve en traces dans la molybdénite (MoS₂), dont il est récupéré comme sous-produit. Chili et Kazakhstan sont les principaux producteurs. Il a le 2e point de fusion le plus élevé des métaux (3186°C) après le tungstène.",
    appearance: "Molybdénite rhenifère : feuillets gris argenté à éclat métallique, indiscernables de la molybdénite pure",
    mainMinerals: [
      { name: "Rhénium dans molybdénite", formula: "MoS₂ (trace Re)" },
      { name: "Dzhezkazganite", formula: "ReCuS₄" },
    ],
    crystalType: 'native_nonmetal',
    mineralColor: '#808888',
    metalness: 0.8,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet5', row: 2, col: 2, rows: 4, cols: 4, label: 'Molybdénite rhenifère (Re)' }],
  },
  76: {
    description: "L'osmium est le métal le plus dense connu (22.59 g/cm³). Il se trouve en traces dans les minerais de platine sous forme d'osmiridium (alliage Os-Ir naturel). L'Afrique du Sud et la Russie sont les producteurs. Son oxyde OsO₄ est extrêmement toxique.",
    appearance: "Osmium natif : petits grains ou lamelles gris-bleu métallique avec un léger reflet bleuté caractéristique",
    mainMinerals: [
      { name: "Osmium natif", formula: "Os" },
      { name: "Osmiridium (névianskite)", formula: "Os-Ir (alliage)" },
      { name: "Erlichmanite", formula: "OsS₂" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#5080a0',
    metalness: 0.95,
    roughness: 0.1,
    sprites: [{ sheet: 'sheet5', row: 2, col: 3, rows: 4, cols: 4, label: 'Osmium natif (Os)' }],
  },
  77: {
    description: "L'iridium est le métal le plus résistant à la corrosion. Il se trouve en traces dans les minerais de platine et dans les couches d'argile marquant la limite K-Pg (extinction des dinosaures, −66 Ma). L'Afrique du Sud produit 85% de l'iridium mondial.",
    appearance: "Iridium natif : petits grains ou pépites blanc-argenté très brillants et extrêmement durs",
    mainMinerals: [
      { name: "Iridium natif", formula: "Ir" },
      { name: "Osmiridium", formula: "Os-Ir (alliage)" },
      { name: "Irarsite", formula: "IrAsS" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#d0d8e0',
    metalness: 0.99,
    roughness: 0.04,
    sprites: [{ sheet: 'sheet5', row: 3, col: 0, rows: 4, cols: 4, label: 'Iridium natif (Ir)' }],
  },
  78: {
    description: "Le platine se trouve à l'état natif sous forme de grains ou pépites dans des roches ultrabasiques (dunites, péridotites) et des placers alluviaux. La Russie (Norilsk) et l'Afrique du Sud (Bushveld) fournissent 80% de la production. C'est l'un des métaux les plus rares et précieux.",
    appearance: "Platine natif : grains ou pépites argentés, très brillants, extrêmement denses (21.4 g/cm³)",
    mainMinerals: [
      { name: "Platine natif", formula: "Pt" },
      { name: "Sperrylite", formula: "PtAs₂" },
      { name: "Coopérite", formula: "PtS" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#e5e4e2',
    metalness: 0.99,
    roughness: 0.04,
    sprites: [{ sheet: 'sheet3', row: 0, col: 1, rows: 4, cols: 4, label: 'Platine natif (Pt)' }],
  },
  79: {
    description: "L'or est l'un des rares métaux qui se trouve presque toujours à l'état natif. Il se présente sous forme de filons dans le quartz, de pépites alluviales ou de grains microscopiques dans les sulfures aurifères. La Chine, l'Australie et la Russie sont les principaux producteurs. Inaltérable et reconnaissable à sa couleur unique.",
    appearance: "Or natif : pépites, paillettes ou filaments d'un jaune éclatant caractéristique, très brillant et dense",
    mainMinerals: [
      { name: "Or natif", formula: "Au" },
      { name: "Calaverite (tellurure d'or)", formula: "AuTe₂" },
      { name: "Électrum (alliage or-argent)", formula: "Au-Ag" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#ffd700',
    metalness: 0.99,
    roughness: 0.05,
    sprites: [{ sheet: 'sheet1', row: 1, col: 0, rows: 4, cols: 4, label: 'Or natif (Au)' }],
  },
  80: {
    description: "Le mercure est le seul métal liquide à température ambiante. Son principal minerai est le cinabre (HgS), un sulfure rouge vif magnifique. Les grandes mines sont à Almadén (Espagne, exploitée depuis 3000 ans) et en Chine. Très toxique, son usage est désormais très réglementé.",
    appearance: "Cinabre : cristaux trigonaux rouge vif à éclat adamantin. Mercure natif : gouttelettes liquides argentées très denses.",
    mainMinerals: [
      { name: "Cinabre", formula: "HgS" },
      { name: "Mercure natif", formula: "Hg (liquide)" },
      { name: "Livingstonite", formula: "HgSb₄S₈" },
    ],
    crystalType: 'liquid',
    mineralColor: '#c0c0c0',
    metalness: 0.99,
    roughness: 0.02,
    sprites: [
      { sheet: 'sheet3', row: 1, col: 2, rows: 4, cols: 4, label: 'Cinabre (HgS)' },
      { sheet: 'sheet3', row: 3, col: 2, rows: 4, cols: 4, label: 'Mercure métallique (Hg)' },
    ],
  },
  81: {
    description: "Le thallium est un métal lourd toxique, rare et dispersé dans les sulfures de plomb et de zinc. La crookésite est son minerai le plus caractéristique. Le thallium est utilisé en optoélectronique et a été employé comme poison (très toxique, inodore, incolore).",
    appearance: "Crookésite : masses grises à éclat métallique, ressemblant à la galène mais plus rare",
    mainMinerals: [
      { name: "Crookésite", formula: "(Cu,Tl,Ag)₂Se" },
      { name: "Lorandite", formula: "TlAsS₂" },
      { name: "Hutchinsonite", formula: "(Tl,Pb)₂As₅S₉" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#909090',
    metalness: 0.6,
    roughness: 0.4,
    sprites: [{ sheet: 'sheet5', row: 3, col: 1, rows: 4, cols: 4, label: 'Crookésite (Tl/Cu/Ag Se)' }],
  },
  82: {
    description: "Le plomb se trouve principalement dans la galène, un sulfure de plomb gris métallique cubique très lourd et brillant. C'est l'un des minerais les plus caractéristiques avec son clivage parfait en cubes. La galène est aussi source d'argent. La Chine, l'Australie et les États-Unis sont les principaux producteurs.",
    appearance: "Galène : cristaux cubiques gris métallique brillant, clivage parfait en cubes, très lourds (7.6 g/cm³)",
    mainMinerals: [
      { name: "Galène", formula: "PbS" },
      { name: "Cérusite", formula: "PbCO₃" },
      { name: "Anglésite", formula: "PbSO₄" },
    ],
    crystalType: 'cluster_cubic',
    mineralColor: '#708090',
    metalness: 0.7,
    roughness: 0.3,
    sprites: [{ sheet: 'sheet3', row: 1, col: 1, rows: 4, cols: 4, label: 'Galène (PbS)' }],
  },
  83: {
    description: "Le bismuth natif forme des cristaux en 'escalier' (cristaux hopper) avec des reflets irisés arc-en-ciel spectaculaires — rouge, bleu, jaune, vert — dus à l'oxydation en fine couche de surface. Ces cristaux sont synthétisés pour la collection, mais le bismuth natif existe aussi en nature. Insolite et décoratif.",
    appearance: "Bismuth natif : cristaux géométriques en escaliers aux reflets irisés multicolores spectaculaires (arc-en-ciel)",
    mainMinerals: [
      { name: "Bismuth natif", formula: "Bi" },
      { name: "Bismuthinite", formula: "Bi₂S₃" },
      { name: "Bismutite", formula: "Bi₂(CO₃)O₂" },
    ],
    crystalType: 'cluster_cubic',
    mineralColor: '#ff69b4',
    metalness: 0.8,
    roughness: 0.1,
    sprites: [{ sheet: 'sheet2', row: 3, col: 3, rows: 4, cols: 4, label: 'Bismuth cristallisé (Bi)' }],
  },
  84: {
    description: "Le polonium a été découvert en 1898 par Marie Curie, qui lui donna le nom de sa patrie. Extrêmement radioactif (émetteur α intense), 1 gramme de ²¹⁰Po génère 140 watts de chaleur. Utilisé dans les éliminateurs d'électricité statique industriels. Célèbre pour l'assassinat d'Alexandre Litvinenko en 2006.",
    appearance: "Minuscule disque métallique argenté dans un conteneur blindé, halo radioactif orange-doré intense",
    mainMinerals: [
      { name: "Uraninite (trace Po)", formula: "UO₂ (trace ²¹⁰Po)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#e0a020',
    metalness: 0.85,
    roughness: 0.15,
    sprites: [{ sheet: 'sheet9', row: 0, col: 2, rows: 4, cols: 4, label: 'Polonium (halo orange intense)' }],
  },
  85: {
    description: "L'astate est l'élément naturel le plus rare sur Terre : moins de 25 grammes existent dans toute la croûte terrestre. Halogène radioactif (demi-vie max : 8,1 h pour ²¹¹At), il est produit en accélérateur de particules. Étudié pour la thérapie alpha ciblée contre le cancer (²¹¹At couplé à des anticorps).",
    appearance: "Concept cristal noir-violet irisé ultra-rare, reflet métallique sombre, presque invisible, fragment nanométrique",
    mainMinerals: [],
    crystalType: 'native_nonmetal',
    mineralColor: '#4a0080',
    metalness: 0.4,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet9', row: 0, col: 3, rows: 4, cols: 4, label: 'Astate (cristal noir-violet)' }],
  },
  86: {
    description: "Le radon est un gaz noble radioactif (α), produit par désintégration du radium-226 dans la chaîne de l'uranium. Il s'accumule dans les sous-sols et caves des maisons construites sur granite ou uranium, constituant un risque sanitaire (2e cause de cancer du poumon). Demi-vie : 3,82 jours.",
    appearance: "Gaz incolore radioactif (émet une faible lueur jaune-orangé sous décharge, invisible à l'œil nu)",
    mainMinerals: [],
    crystalType: 'gas',
    mineralColor: '#c8b040',
    metalness: 0,
    roughness: 1,
    sprites: [{ sheet: 'sheet8', row: 2, col: 1, rows: 4, cols: 4, label: 'Tube décharge Rn (lueur éerie)' }],
  },
  87: {
    description: "Le francium est l'élément naturel le plus rare sur Terre : à tout instant, moins de 30 grammes existent dans la croûte terrestre. Produit par désintégration de l'actinium-227, il est extrêmement radioactif (demi-vie : 22 minutes). Jamais isolé en quantité macroscopique. Le plus instable des alcalins.",
    appearance: "Métal alcalin hypothétique argenté (jamais observé en masse) — concept artistique : gouttelette métallique dans une ampoule avec lueur bleue radioactive",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#4060c0',
    metalness: 0.9,
    roughness: 0.1,
    sprites: [{ sheet: 'sheet8', row: 3, col: 2, rows: 4, cols: 4, label: 'Francium — concept (lueur bleue)' }],
  },
  88: {
    description: "Le radium a été découvert en 1898 par Marie et Pierre Curie dans la pechblende. Métal alcalino-terreux blanc brillant, très radioactif (α et γ), il était utilisé en médecine avant que sa dangerosité soit reconnue. Toute peinture luminescente au radium a été interdite dans les années 1960. Demi-vie Ra-226 : 1600 ans.",
    appearance: "Cristaux blancs luminescents dans une fiole en verre scellée, émettant une lueur bleu-blanc fantomatique dans l'obscurité",
    mainMinerals: [
      { name: "Pechblende (uraninite)", formula: "UO₂ (trace Ra)" },
      { name: "Carnotite", formula: "K₂(UO₂)₂(VO₄)₂·3H₂O (trace Ra)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#b0d8f8',
    metalness: 0.7,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet8', row: 3, col: 3, rows: 4, cols: 4, label: 'Radium cristallisé (lueur bleue)' }],
  },
  89: {
    description: "L'actinium a été découvert en 1899 par André-Louis Debierne. Métal argenté brillant extrêmement radioactif, il émet une lueur bleue fantomatique dans l'obscurité (rayonnement Cherenkov). Précurseur du ²²⁵Ac, prometteur en médecine nucléaire pour la thérapie alpha ciblée contre les cancers.",
    appearance: "Ampoule scellée avec métal argenté émettant une lueur bleu-blanc spectrale caractéristique, effet Cherenkov visible",
    mainMinerals: [
      { name: "Uraninite (trace Ac)", formula: "UO₂ (trace ²²⁷Ac)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#80d0ff',
    metalness: 0.85,
    roughness: 0.1,
    sprites: [{ sheet: 'sheet9', row: 1, col: 0, rows: 4, cols: 4, label: 'Actinium (lueur Cherenkov bleue)' }],
  },
  90: {
    description: "Le thorium est un actinide faiblement radioactif (α), abondant dans la monazite et la thorite. Son abondance est comparable à celle du plomb. L'Inde, le Brésil et l'Australie possèdent d'importants gisements. Il pourrait constituer un combustible nucléaire alternatif à l'uranium.",
    appearance: "Thorite : cristaux tétragonaux brun-rouge à noirs, éclat vitreux à résineux, souvent métamictes",
    mainMinerals: [
      { name: "Thorite", formula: "ThSiO₄" },
      { name: "Monazite (avec Th)", formula: "(Ce,La,Th)PO₄" },
      { name: "Thorianite", formula: "ThO₂" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#808060',
    metalness: 0.3,
    roughness: 0.6,
    sprites: [{ sheet: 'sheet5', row: 3, col: 2, rows: 4, cols: 4, label: 'Thorite (ThSiO₄)' }],
  },
  91: {
    description: "Le protactinium est un actinide rare et hautement radioactif, présent en traces dans la pechblende (0,34 ppm). Métal argenté dense, il est l'un des éléments naturels les plus rares et toxiques. Peu d'applications pratiques en raison de sa radioactivité et sa rareté. Intermédiaire de désintégration de l'uranium-235.",
    appearance: "Lingot métallique argenté brillant dans un conteneur hermétique, légère aura radioactive argentée-bleue",
    mainMinerals: [
      { name: "Uraninite (trace Pa)", formula: "UO₂ (trace ²³¹Pa)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#a0b0c0',
    metalness: 0.9,
    roughness: 0.1,
    sprites: [{ sheet: 'sheet9', row: 1, col: 1, rows: 4, cols: 4, label: 'Protactinium (lingot argenté)' }],
  },
  92: {
    description: "L'uranium se trouve principalement dans l'uraninite (pechblende), un oxyde noir brillant. Des minéraux secondaires colorés comme l'autunite (jaune-vert fluorescent) et la torbernite (vert) se forment par altération en présence d'eau. Le Kazakhstan, le Canada et l'Australie sont les principaux producteurs.",
    appearance: "Uraninite (pechblende) : masses noires grenues à éclat gras. Autunite : cristaux jaune-vert fluorescents sous UV.",
    mainMinerals: [
      { name: "Uraninite (pechblende)", formula: "UO₂" },
      { name: "Autunite", formula: "Ca(UO₂)₂(PO₄)₂·12H₂O" },
      { name: "Carnotite", formula: "K₂(UO₂)₂(VO₄)₂·3H₂O" },
    ],
    crystalType: 'rough_ore',
    mineralColor: '#33cc33',
    metalness: 0.2,
    roughness: 0.7,
    sprites: [{ sheet: 'sheet2', row: 3, col: 2, rows: 4, cols: 4, label: 'Uraninite / Autunite (U)' }],
  },
  93: {
    description: "Le neptunium est le premier élément transuranien, synthétisé en 1940. On en trouve des traces dans l'uraninite par réaction neutronique naturelle. Métal argenté légèrement doré, radioactif. Intermédiaire de production du plutonium dans les réacteurs nucléaires.",
    appearance: "Lingot métallique argenté légèrement doré dans un conteneur blindé, aura radioactive chaude dorée",
    mainMinerals: [
      { name: "Uraninite (trace Np)", formula: "UO₂ (trace ²³⁷Np)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#c0b060',
    metalness: 0.88,
    roughness: 0.12,
    sprites: [{ sheet: 'sheet9', row: 1, col: 2, rows: 4, cols: 4, label: 'Neptunium (lingot argenté-doré)' }],
  },
  94: {
    description: "Le plutonium est l'actinide le plus connu, tristement célèbre pour la bombe atomique. Métal lourd argenté-doré qui s'oxyde en surface en prenant des teintes dorées et violacées irisées. Combustible des réacteurs à neutrons rapides et des générateurs RTG spatiaux (sondes Voyager, Cassini). Le ²³⁹Pu est fissile.",
    appearance: "Sphère ou lingot métal argenté-doré chaud avec surface légèrement oxydée irisée (violet, doré, bronze), halo radioactif ambré",
    mainMinerals: [
      { name: "Uraninite (trace Pu)", formula: "UO₂ (trace ²³⁹Pu)" },
    ],
    crystalType: 'native_metal',
    mineralColor: '#d4a030',
    metalness: 0.85,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet9', row: 1, col: 3, rows: 4, cols: 4, label: 'Plutonium (sphère dorée-irisée)' }],
  },
  95: {
    description: "L'américium est produit dans les réacteurs par capture neutronique du plutonium. Métal argenté, il est présent dans chaque détecteur de fumée ionique (²⁴¹Am) à raison de ~0,3 µg. L'²⁴³Am est la source pour produire du curium. Radioactif α et γ.",
    appearance: "Minuscule disque gris argenté dans la chambre d'ionisation d'un détecteur de fumée, légère aura argent-bleue",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#b0b8d0',
    metalness: 0.85,
    roughness: 0.15,
    sprites: [{ sheet: 'sheet9', row: 2, col: 0, rows: 4, cols: 4, label: 'Américium (détecteur de fumée)' }],
  },
  96: {
    description: "Le curium est nommé en hommage à Marie et Pierre Curie. Métal argenté brillant produit par bombardement du plutonium. Ses isotopes ²⁴²Cm et ²⁴⁴Cm génèrent une chaleur importante, utilisée dans les générateurs RTG (sonde Mars Pathfinder). Émet une lueur orange chaude par excitation.",
    appearance: "Ampoule de verre contenant un lingot argenté brillant émettant une lueur orange chaude radioactive, hommage aux Curie",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#e08040',
    metalness: 0.88,
    roughness: 0.12,
    sprites: [{ sheet: 'sheet9', row: 2, col: 1, rows: 4, cols: 4, label: 'Curium (lueur orange chaude)' }],
  },
  97: {
    description: "Le berkélium a été synthétisé en 1949 au Lawrence Berkeley National Laboratory. Métal argenté produit par bombardement d'américium. Seulement quelques milligrammes ont été produits au total. Utilisé uniquement pour la recherche fondamentale et comme cible pour synthétiser des éléments plus lourds (californium, einsteinium).",
    appearance: "Minuscule grain métallique argenté sur une plateforme de recherche nucléaire, aura pourpre radioactive subtile",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#9060a0',
    metalness: 0.88,
    roughness: 0.12,
    sprites: [{ sheet: 'sheet9', row: 2, col: 2, rows: 4, cols: 4, label: 'Berkélium (grain lab, aura pourpre)' }],
  },
  98: {
    description: "Le californium est l'un des émetteurs de neutrons les plus intenses connus. Quelques microgrammes de ²⁵²Cf suffisent à démarrer un réacteur nucléaire. Utilisé dans la prospection pétrolière, le traitement de certains cancers par neutronthérapie et la détection d'explosifs. Produit à Oak Ridge (USA) et Dimitrovgrad (Russie).",
    appearance: "Micro-source de neutrons dans un conteneur blindé émettant une lumière bleu-blanc intense, halo Cherenkov puissant",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#60c0ff',
    metalness: 0.8,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet9', row: 2, col: 3, rows: 4, cols: 4, label: 'Californium (source neutrons)' }],
  },
  99: {
    description: "L'einsteinium a été découvert en 1952 dans les retombées de la première bombe thermonucléaire (Ivy Mike). Nommé en hommage à Albert Einstein. Seuls quelques microgrammes ont jamais été produits. Les propriétés chimiques de l'²⁵³Es ont été étudiées récemment pour la première fois (2021, Lawrence Berkeley Lab).",
    appearance: "Concept artistique : minuscule cristal rose-rouge luminescent dans une fiole de laboratoire, hommage à Einstein",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#e04060',
    metalness: 0.7,
    roughness: 0.3,
    sprites: [{ sheet: 'sheet9', row: 3, col: 0, rows: 4, cols: 4, label: 'Einsteinium (cristal rose-rouge)' }],
  },
  100: {
    description: "Le fermium a été découvert en 1952 dans les mêmes retombées nucléaires que l'einsteinium. Nommé en hommage à Enrico Fermi. Jamais produit en quantité visible. Limite pratique de la synthèse par neutrons : au-delà du fermium, les noyaux fissionnent trop rapidement. Seulement quelques centaines d'atomes ont été étudiés.",
    appearance: "Concept : particules luminescentes vert radioactif dans un conteneur de laboratoire, hommage à Fermi",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#40c060',
    metalness: 0.7,
    roughness: 0.3,
    sprites: [{ sheet: 'sheet9', row: 3, col: 1, rows: 4, cols: 4, label: 'Fermium (lueur verte lab)' }],
  },
  101: {
    description: "Le mendélévium a été synthétisé en 1955 par Ghiorso, Harvey, Choppin, Thompson et Seaborg. Nommé en hommage à Dmitri Mendeleïev, créateur de la table périodique. Produit atome par atome par bombardement d'einsteinium. Seuls quelques dizaines d'atomes ont jamais été observés.",
    appearance: "Concept hommage à Mendeleïev : lueur bleu-violet énigmatique dans une ampoule, évocation de la table périodique",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#6080e0',
    metalness: 0.7,
    roughness: 0.3,
    sprites: [{ sheet: 'sheet9', row: 3, col: 2, rows: 4, cols: 4, label: 'Mendélévium (lueur bleu-violet)' }],
  },
  102: {
    description: "Le nobélium a été synthétisé en 1966 (confirmé) par des équipes soviétiques à Doubna. Nommé en hommage à Alfred Nobel. Produit par fusion nucléaire de curium et de carbone-12. Tous ses isotopes sont radioactifs, le plus stable (²⁵⁹No) ayant une demi-vie de 58 minutes. Seuls quelques atomes ont été produits.",
    appearance: "Concept : lumière froide blanc cristallin dans une ampoule scellée, hommage à Nobel, lueur radioactive pâle",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#e0e8f8',
    metalness: 0.7,
    roughness: 0.3,
    sprites: [{ sheet: 'sheet9', row: 3, col: 3, rows: 4, cols: 4, label: 'Nobélium (lueur blanche froide)' }],
  },
  103: {
    description: "Le lawrencium est le dernier actinide, synthétisé en 1961 au Lawrence Berkeley Laboratory. Nommé en hommage à Ernest O. Lawrence, inventeur du cyclotron. Produit par bombardement de californium par des ions bore. Demi-vie du ²⁶²Lr : 3,6 heures. Seuls quelques atomes ont été étudiés.",
    appearance: "Concept cyclotron : faisceau de particules tourbillonnant en anneau or-blanc, hommage au cyclotron de Lawrence",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#d4a840',
    metalness: 0.85,
    roughness: 0.15,
    sprites: [{ sheet: 'sheet10', row: 0, col: 0, rows: 4, cols: 4, label: 'Lawrencium (faisceau cyclotron)' }],
  },
  104: {
    description: "Le rutherfordium est le premier élément superlourds (transactinide), synthétisé en 1964 à Doubna et 1969 à Berkeley. Nommé en hommage à Ernest Rutherford. Demi-vie du ²⁶⁵Rf : 13 heures. Comportement chimique similaire au hafnium. Produit par fusion de noyaux de curium et de néon.",
    appearance: "Concept noyau atomique : section transversale d'un noyau dense brillant bleu-blanc, hommage à l'expérience de la feuille d'or",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#80b0e0',
    metalness: 0.85,
    roughness: 0.15,
    sprites: [{ sheet: 'sheet10', row: 0, col: 1, rows: 4, cols: 4, label: 'Rutherfordium (noyau bleu-blanc)' }],
  },
  105: {
    description: "Le dubnium a été synthétisé simultanément à Doubna (Russie) et Berkeley (USA) en 1968-1970, donnant lieu à une longue dispute de priorité dite « guerre des transfermiums ». Nommé d'après la ville de Doubna. Demi-vie du ²⁶⁸Db : 29 heures. Comportement similaire au tantale.",
    appearance: "Concept laboratoire soviétique : cible d'accélérateur disque bleu-gris froid, esthétique Doubna avec reflet rouge subtil",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#607090',
    metalness: 0.85,
    roughness: 0.15,
    sprites: [{ sheet: 'sheet10', row: 0, col: 2, rows: 4, cols: 4, label: 'Dubnium (cible accélérateur)' }],
  },
  106: {
    description: "Le seaborgium a été synthétisé en 1974 à Berkeley. Nommé en hommage à Glenn T. Seaborg, qui fut le seul scientifique vivant à avoir un élément nommé d'après lui de son vivant. Demi-vie du ²⁶⁹Sg : 14 minutes. Comportement chimique similaire au tungstène.",
    appearance: "Concept réaction nucléaire : éclair d'énergie argent-vert, hommage à Seaborg et à la chimie des transuraniens",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#70a870',
    metalness: 0.85,
    roughness: 0.15,
    sprites: [{ sheet: 'sheet10', row: 0, col: 3, rows: 4, cols: 4, label: 'Seaborgium (éclair argent-vert)' }],
  },
  107: {
    description: "Le bohrium a été synthétisé en 1981 au GSI Helmholtz Centre (Darmstadt, Allemagne). Nommé en hommage à Niels Bohr. Produit par fusion de bismuth-209 et de chrome-54. Demi-vie du ²⁷⁰Bh : 61 secondes. Comportement similaire au rhénium.",
    appearance: "Concept mécanique quantique : motif d'interférence d'ondes cyano-blanc, évocation du modèle atomique de Bohr",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#40c8c8',
    metalness: 0.8,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet10', row: 1, col: 0, rows: 4, cols: 4, label: 'Bohrium (ondes quantiques cyan)' }],
  },
  108: {
    description: "Le hassium a été synthétisé en 1984 au GSI (Allemagne). Nommé d'après la Hesse (Hassia en latin). Prédit comme le métal le plus dense jamais synthétisé (~41 g/cm³). Demi-vie du ²⁶⁹Hs : 9,7 secondes. Comportement similaire à l'osmium. Seul transactinide dont l'oxyde volatile a été étudié.",
    appearance: "Gouttelette de métal ultra-dense argenté avec reflet émeraude, métal le plus lourd prévu, esthétique Hesse allemande",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#308060',
    metalness: 0.92,
    roughness: 0.08,
    sprites: [{ sheet: 'sheet10', row: 1, col: 1, rows: 4, cols: 4, label: 'Hassium (métal ultra-dense vert)' }],
  },
  109: {
    description: "Le meitnerium a été synthétisé en 1982 au GSI. Nommé en hommage à Lise Meitner, physicienne autrichienne co-découvreuse de la fission nucléaire, injustement écartée du prix Nobel. Demi-vie du ²⁷⁸Mt : 7,6 secondes. Comportement prédit similaire à l'iridium.",
    appearance: "Noyau atomique se scindant en deux, lueur rose-or élégante, hommage à la découverte de la fission par Lise Meitner",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#e090a0',
    metalness: 0.8,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet10', row: 1, col: 2, rows: 4, cols: 4, label: 'Meitnerium (fission rose-or)' }],
  },
  110: {
    description: "Le darmstadtium a été synthétisé en 1994 au GSI de Darmstadt. Nommé d'après la ville de Darmstadt. Produit par fusion de nickel-62 et de plomb-208. Demi-vie du ²⁸¹Ds : 12,7 secondes. Comportement prédit similaire au platine. Seuls quelques atomes ont été produits.",
    appearance: "Point d'impact de collision de faisceau de particules, éclair blanc-argent intense avec couronne brillante, GSI Darmstadt",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#d0d8e8',
    metalness: 0.9,
    roughness: 0.1,
    sprites: [{ sheet: 'sheet10', row: 1, col: 3, rows: 4, cols: 4, label: 'Darmstadtium (impact blanc-argent)' }],
  },
  111: {
    description: "Le roentgenium a été synthétisé en 1994 au GSI. Nommé en hommage à Wilhelm Röntgen, découvreur des rayons X. Demi-vie du ²⁸²Rg : 100 secondes. Comportement prédit similaire à l'or mais avec des propriétés relativistes marquées rendant sa chimie difficile à prédire.",
    appearance: "Esthétique rayons X : structure cristalline éclairée en bleu-blanc froid, évocation de la radiographie, hommage à Röntgen",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#a0c8e8',
    metalness: 0.85,
    roughness: 0.15,
    sprites: [{ sheet: 'sheet10', row: 2, col: 0, rows: 4, cols: 4, label: 'Roentgenium (esthétique rayons X)' }],
  },
  112: {
    description: "Le copernicium a été synthétisé en 1996 au GSI. Nommé en hommage à Nicolas Copernic. Prédit comme gaz noble à température ambiante malgré d'être un métal — effet relativiste extrême. Demi-vie du ²⁸⁵Cn : 28 secondes. Dernier élément dont la chimie en phase gazeuse a été étudiée expérimentalement.",
    appearance: "Vapeur argentée-bleue éthérée tourbillonnant comme un système planétaire, évocation de Copernic, métal prédit gazeux",
    mainMinerals: [],
    crystalType: 'gas',
    mineralColor: '#8090d0',
    metalness: 0.3,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet10', row: 2, col: 1, rows: 4, cols: 4, label: 'Copernicium (vapeur argentée-bleue)' }],
  },
  113: {
    description: "Le nihonium a été synthétisé en 2004 au RIKEN (Japon). Premier élément découvert en Asie. Nommé d'après le Japon (Nihon en japonais). Demi-vie du ²⁸⁶Nh : 9,5 secondes. Comportement prédit similaire au thallium. La confirmation a pris 9 ans et plus de 400 milliards de tentatives de fusion.",
    appearance: "Lueur atomique rose-or délicat, esthétique laboratoire RIKEN japonais, évocation des fleurs de cerisier à l'échelle atomique",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#e8b0a0',
    metalness: 0.8,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet10', row: 2, col: 2, rows: 4, cols: 4, label: 'Nihonium (lueur rose-or japonais)' }],
  },
  114: {
    description: "Le flérovium a été synthétisé en 1999 au laboratoire Flerov de Doubna. Nommé en hommage à Gueorgui Flerov. Comportement prédit de gaz noble par effets relativistes, malgré son appartenance au groupe du plomb. Demi-vie du ²⁸⁹Fl : 2,6 secondes. Seuls quelques atomes ont été produits.",
    appearance: "Concept gaz noble-métal : vapeur violet-argent mystérieuse, esthétique Doubna, comportement imprévisible dû aux effets relativistes",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#9060c0',
    metalness: 0.5,
    roughness: 0.4,
    sprites: [{ sheet: 'sheet10', row: 2, col: 3, rows: 4, cols: 4, label: 'Flérovium (vapeur violet-argent)' }],
  },
  115: {
    description: "Le moscovium a été synthétisé en 2003 conjointement par Doubna (Russie) et Livermore (USA). Nommé d'après l'Oblast de Moscou. Demi-vie du ²⁸⁸Mc : 87 millisecondes. Comportement prédit similaire au bismuth. Précurseur du nihonium dans sa chaîne de désintégration.",
    appearance: "Halo radioactif or-ambré chaud, esthétique russe, collaboration scientifique internationale symbolisée",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#d09030',
    metalness: 0.8,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet10', row: 3, col: 0, rows: 4, cols: 4, label: 'Moscovium (halo or ambré)' }],
  },
  116: {
    description: "Le livermorium a été synthétisé en 2000 en collaboration entre Doubna et le Lawrence Livermore National Laboratory (Californie). Nommé d'après Livermore. Demi-vie du ²⁹³Lv : 57 millisecondes. Comportement prédit similaire au polonium. Ses produits de désintégration incluent le flérovium.",
    appearance: "Collision de faisceaux en éclair orange-rouge californien, esthétique Livermore, chaleur intense de la réaction nucléaire",
    mainMinerals: [],
    crystalType: 'native_metal',
    mineralColor: '#e06030',
    metalness: 0.8,
    roughness: 0.2,
    sprites: [{ sheet: 'sheet10', row: 3, col: 1, rows: 4, cols: 4, label: 'Livermorium (éclair orange-rouge)' }],
  },
  117: {
    description: "Le tennessine a été synthétisé en 2010 en collaboration entre Doubna, Oak Ridge, Vanderbilt et le Lawrence Livermore. Nommé d'après le Tennessee. Demi-vie du ²⁹⁴Ts : 51 millisecondes. Prédit comme halogène avec comportement métallique. Avant-dernier élément confirmé de la table périodique.",
    appearance: "Vapeur violette-pourpre halogène synthétique, esthétique Tennessee (ORNL), derniers halogènes de la table périodique",
    mainMinerals: [],
    crystalType: 'native_nonmetal',
    mineralColor: '#8040a0',
    metalness: 0.4,
    roughness: 0.5,
    sprites: [{ sheet: 'sheet10', row: 3, col: 2, rows: 4, cols: 4, label: 'Tennessine (vapeur violette)' }],
  },
  118: {
    description: "L'oganesson est le dernier élément confirmé de la table périodique, synthétisé en 2002 à Doubna. Nommé en hommage à Yuri Oganessian, père des éléments superlourds. Demi-vie du ²⁹⁴Og : 0,89 milliseconde. Théoriquement un gaz noble solide à température ambiante par effets relativistes. La frontière de la matière connue.",
    appearance: "Pourpre cosmique profond avec étincelles quantiques blanches, fin ultime de la table périodique, mystère de la matière à ses limites",
    mainMinerals: [],
    crystalType: 'gas',
    mineralColor: '#3a1060',
    metalness: 0.2,
    roughness: 0.8,
    sprites: [{ sheet: 'sheet10', row: 3, col: 3, rows: 4, cols: 4, label: 'Oganesson (fin de la table — cosmic)' }],
  },
};
